import React, { useState } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  message,
  Upload,
  Table,
  Button,
  Alert,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd';
import { updateDesignDocument } from '@/api/requirements';
import { getUploadUrl } from '@/api/storage';
import type { DesignDocument, DesignDocumentUpdateRequest } from '@/types';
import {
  calculateFileMD5,
  validateUploadResponse,
  verifyUploadedFile,
  cleanupFailedUpload,
} from '@/utils/uploadUtils';
import { uploadLog, uploadError } from '@/utils/uploadLogger';

const { TextArea } = Input;

interface DesignDocumentUpdateModalProps {
  visible: boolean;
  document: DesignDocument | null;
  projectId: number;
  onCancel: () => void;
  onSuccess: (urls: string[]) => void;
}

const DesignDocumentUpdateModal: React.FC<DesignDocumentUpdateModalProps> = ({
  visible,
  document,
  projectId,
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState<Set<string>>(new Set());

  React.useEffect(() => {
    if (document && visible) {
      form.setFieldsValue({
        file_description: document.file_description,
        features: document.features,
        requirement_attribute: document.requirement_attribute,
        documents: document.documents.join('\n'),
        remarks: document.remarks,
        user_version: document.user_version || '',
        version_remarks: '',
      });
      setFileList([]);
    }
  }, [document, visible, form]);

  const parseDocumentUrls = (text: string): string[] => {
    return text
      .split('\n')
      .map((url) => url.trim())
      .filter((url) => url.length > 0);
  };

  const handleUploadChange = ({ fileList: newFileList }: { fileList: UploadFile[] }) => {
    setFileList(newFileList);
  };

  const handleUploadBeforeUpload = async (_file: File) => {
    // Prevent automatic upload, we'll handle it manually
    return false;
  };

  const uploadAttachments = async (): Promise<any[]> => {
    const uploaded: any[] = [];
    const failedObjectKeys: string[] = []; // Track failed uploads for cleanup

    for (const uploadFile of fileList) {
      // Only process files with originFileObj (newly selected files)
      if (!uploadFile.originFileObj) {
        continue;
      }

      const fileToUpload = uploadFile.originFileObj as File;

      try {
        setUploadingFiles((prev) => new Set([...prev, uploadFile.uid]));

        // 1. Calculate MD5 hash for file integrity verification
        const md5 = await calculateFileMD5(fileToUpload);
        uploadLog(`[Upload:MD5] ${uploadFile.name}: ${md5}`);

        // 2. Request signed upload URL from backend
        // Note: Frontend adds "design-documents/{timestamp}_" prefix to distinguish
        // design doc uploads from other file uploads
        const signedUrlResponse = await getUploadUrl(
          `design-documents/${Date.now()}_${uploadFile.name}`,
          fileToUpload.type || 'application/octet-stream'
        );
        const objectKey = signedUrlResponse.object_key;
        uploadLog(
          `[Upload:SignedUrl] ${uploadFile.name}: object_key=${objectKey}`
        );

        // 3. Upload file directly to OBS with MD5 validation
        const response = await fetch(signedUrlResponse.url, {
          method: 'PUT',
          body: fileToUpload,
          headers: {
            'Content-Type': fileToUpload.type || 'application/octet-stream',
          },
        });

        // 4. Validate upload response (status code, ETag, MD5 match)
        await validateUploadResponse(response, md5, objectKey, uploadFile.name);
        uploadLog(
          `[Upload:Validated] ${uploadFile.name}: ETag matches MD5`
        );

        // 5. Verify file exists in OBS (backend verification)
        try {
          await verifyUploadedFile(
            objectKey,
            fileToUpload.size,
            uploadFile.name
          );
        } catch (verifyError) {
          uploadError(
            `[Upload:VerificationFailed] ${uploadFile.name}:`,
            verifyError
          );
          failedObjectKeys.push(objectKey);
          throw new Error(
            `File verification failed after upload: ${
              verifyError instanceof Error ? verifyError.message : 'Unknown error'
            }`
          );
        }

        // 6. All validations passed - record for database creation
        uploaded.push({
          file_name: uploadFile.name,
          file_url: objectKey, // Store object_key, not full URL (consistent with main page)
          object_key: objectKey,
          file_size: fileToUpload.size,
        });

        uploadLog(`[Upload:Success] ${uploadFile.name} uploaded and verified`);
        message.success(`${uploadFile.name} 上传成功`);
      } catch (error) {
        uploadError(`[Upload:Failed] ${uploadFile.name}:`, error);
        message.error(
          `${uploadFile.name} 上传失败: ${
            error instanceof Error ? error.message : 'Unknown error'
          }`
        );
      } finally {
        setUploadingFiles((prev) => {
          const newSet = new Set(prev);
          newSet.delete(uploadFile.uid);
          return newSet;
        });
      }
    }

    // Cleanup any failed uploads from OBS
    for (const objectKey of failedObjectKeys) {
      try {
        await cleanupFailedUpload(objectKey, 'unknown');
      } catch {
        // Cleanup failures already logged
      }
    }

    return uploaded;
  };

  const handleSubmit = async () => {
    if (!document) return;

    try {
      const values = await form.validateFields();
      const documentUrls = parseDocumentUrls(values.documents || '');

      setLoading(true);

      // Upload new attachments
      uploadLog('开始上传附件，待上传文件数:', fileList.length);
      const newAttachments = await uploadAttachments();
      uploadLog('附件上传完成，成功上传:', newAttachments.length, '个', newAttachments);

      const payload: DesignDocumentUpdateRequest = {
        file_description: values.file_description,
        features: values.features,
        requirement_attribute: values.requirement_attribute,
        documents: documentUrls,
        remarks: values.remarks || '',
        user_version: values.user_version || '',
        version_remarks: values.version_remarks,
        new_attachments: newAttachments,
      };

      uploadLog('发送更新请求，payload:', payload);
      await updateDesignDocument(projectId, document.id, payload as any);
      message.success('设计文档更新成功，已创建新版本');

      // 清空文件列表
      setFileList([]);

      onSuccess(documentUrls);
      onCancel();
    } catch (error: any) {
      uploadError('Update failed:', error);
      let errorMsg = '更新失败';

      if (error?.response?.data?.message) {
        errorMsg = error.response.data.message;
      } else if (error?.response?.data?.error) {
        errorMsg = error.response.data.error;
      } else if (error?.message) {
        errorMsg = error.message;
      }

      uploadError('完整错误信息:', error?.response?.data);
      message.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={document ? `更新设计文档 - 文件${document.file_number}` : '更新设计文档'}
      open={visible}
      onOk={handleSubmit}
      onCancel={onCancel}
      width={700}
      okText="提交"
      cancelText="取消"
      confirmLoading={loading}
      okButtonProps={{
        disabled: uploadingFiles.size > 0,
      }}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label="文件描述"
          name="file_description"
          rules={[
            { required: true, message: '请输入文件描述' },
            { min: 5, message: '至少5个字符' },
            { max: 1000, message: '最多1000个字符' },
          ]}
        >
          <TextArea
            rows={3}
            placeholder="请输入文件描述"
            showCount
            maxLength={1000}
          />
        </Form.Item>

        <Form.Item
          label="Features"
          name="features"
          rules={[{ required: true, message: '请选择Features' }]}
        >
          <Select placeholder="请选择">
            <Select.Option value="MCU">MCU</Select.Option>
            <Select.Option value="SOC">SOC</Select.Option>
            <Select.Option value="MCU&SOC">MCU&SOC</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          label="需求属性"
          name="requirement_attribute"
          rules={[{ required: true, message: '请选择需求属性' }]}
        >
          <Select placeholder="请选择">
            <Select.Option value="必选">必选</Select.Option>
            <Select.Option value="可选">可选</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          label="文档链接"
          name="documents"
          help="每行一个URL链接，最多10个"
          rules={[
            {
              validator: (_, value) => {
                if (!value) return Promise.resolve();
                const urls = parseDocumentUrls(value);
                if (urls.length > 10) {
                  return Promise.reject(new Error('最多支持10个文档链接'));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <TextArea
            rows={4}
            placeholder={
              '每行一个URL链接\\nhttps://example.com/doc1\\nhttps://example.com/doc2'
            }
          />
        </Form.Item>

        <Form.Item
          label="备注"
          name="remarks"
        >
          <TextArea rows={2} placeholder="可选" />
        </Form.Item>

        <Form.Item
          label="版本"
          name="user_version"
          help="可选，用于标识本次发布的版本号（如V1.0.0, V2.1.3等）"
          rules={[{ max: 50, message: '版本号最多50个字符' }]}
        >
          <Input
            placeholder="请输入版本号（可选）"
            maxLength={50}
          />
        </Form.Item>

        <Form.Item
          label="更新说明"
          name="version_remarks"
          rules={[
            { required: true, message: '请输入更新说明' },
            { min: 5, message: '至少5个字符' },
          ]}
        >
          <TextArea
            rows={3}
            placeholder="描述本次更新的内容和原因"
            showCount
            maxLength={1000}
          />
        </Form.Item>

        <Form.Item
          label="上传新版本附件"
          help="在此上传的附件将直接归属于新版本"
        >
          <Upload
            fileList={fileList}
            onChange={handleUploadChange}
            beforeUpload={handleUploadBeforeUpload}
            multiple
          >
            <Button icon={<UploadOutlined />}>选择文件</Button>
          </Upload>
          {fileList.length > 0 && (
            <>
              <Alert
                title="提示"
                description="主页面已上传的草稿附件也会自动归档到新版本"
                type="warning"
                showIcon
                style={{ marginTop: 12, marginBottom: 12 }}
              />
              <div style={{ marginTop: 16 }}>
                <p>待上传文件：</p>
                <Table
                  dataSource={fileList}
                  rowKey="uid"
                  size="small"
                  pagination={false}
                  columns={[
                    {
                      title: '文件名',
                      dataIndex: 'name',
                      width: '60%',
                    },
                    {
                      title: '大小',
                      render: (_, record) => {
                        const size = record.size || 0;
                        if (size < 1024) return `${size} B`;
                        if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
                        return `${(size / (1024 * 1024)).toFixed(1)} MB`;
                      },
                      width: '20%',
                    },
                    {
                      title: '状态',
                      render: (_, record) => {
                        if (uploadingFiles.has(record.uid)) return '上传中...';
                        if (record.status === 'done') return '已上传';
                        return '待上传';
                      },
                      width: '20%',
                    },
                  ]}
                />
              </div>
            </>
          )}
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default DesignDocumentUpdateModal;
